import {
  FormAfterSubmission,
  FormCaptchaConfig,
  FormField,
  FormFieldType,
  formFieldTypes,
  MultiChoiceFormField,
  PictureChoiceFormField,
  PostSubmission,
  SelectFormField,
  SimpleForm,
} from '@betterfunnel/types';
import { DialogTitle } from '@headlessui/react';
import {
  CheckIcon,
  PencilIcon,
  TrashIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import { PlusIcon } from '@heroicons/react/24/solid';
import * as Sentry from '@sentry/react';
import { Timestamp } from 'firebase/firestore';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { uid } from 'uid';
import phoneCodes from '../assets/phone-country-codes.json';
import Button from '../components/Common/Button';
import Checkbox from '../components/Common/Checkbox';
import ChipsAutocomplete from '../components/Common/ChipsAutocomplete';
import IconButton from '../components/Common/IconButton';
import Modal, { ModalActionData } from '../components/Common/Modal';
import Select from '../components/Common/Select';
import TextField from '../components/Common/TextField';
import Toggle from '../components/Common/Toggle';
import Tooltip from '../components/Common/Tooltip';
import FileUploadInput from '../components/FormFields/FileUpload/FileUploadInput';
import QuillField from '../components/QuillField';
import SortableList from '../components/SortableList';
import { AuthContext } from '../contexts/AuthContext';
import { SnackbarContext } from '../contexts/SnackbarContext';
import { UserContext } from '../contexts/UserContext';
import {
  createForm as createFormApi,
  updateForm,
} from '../services/FormsService';
import { groupBy } from '../utils';
import {
  getFormFieldTypeIcon,
  getFormFieldTypeLabel,
} from '../utils/form-utils';
import { formFieldUrlValidator } from '../utils/validator';

type EditableFormField = FormField & { _id: string };

const defaultForm = (): UpdatableForm => {
  return {
    name: 'Lead capture form',
    fields: [
      { _id: uid(8), id: 'name', label: 'Name', type: 'text' },
      {
        _id: uid(8),
        id: 'email',
        label: 'Email',
        type: 'email',
        required: true,
      },
      { _id: uid(8), id: 'phone', label: 'Phone', type: 'tel' },
      { _id: uid(8), id: 'message', label: 'Message', type: 'textArea' },
    ],
    buttonTitle: 'Send',
    postSubmission: { action: 'none' },
    captcha: { enabled: false },
  };
};

interface UpdatableForm {
  name?: string;
  fields?: EditableFormField[];
  buttonTitle?: string;
  postSubmission?: Partial<PostSubmission>;
  preActionText?: string;
  afterSubmission?: FormAfterSubmission;
  captcha?: FormCaptchaConfig;
}

interface Props {
  form?: SimpleForm;
  formId?: string;
  open: boolean;
  onClose: () => void;
}

export default function CreateFormModal({
  open,
  onClose,
  form,
  formId,
}: Props) {
  const { show } = useContext(SnackbarContext);
  const { firebaseUser } = useContext(AuthContext);
  const { selectedAccount } = useContext(UserContext);

  const [editId, setEditId] = useState<string>();
  const [data, setData] = useState<UpdatableForm>(() => defaultForm());
  const [creating, setCreating] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [error, setError] = useState<Error>();
  const [editingFields, setEditingFields] = useState<{
    [key: string]: boolean;
  }>({});

  useEffect(() => {
    setData(
      form
        ? {
            name: form.name,
            fields: form.fields?.map((f) => ({ ...f, _id: uid(8) })),
            buttonTitle: form.button.title,
            postSubmission: form.postSubmission,
            preActionText: form.preActionText,
            afterSubmission: form.afterSubmission,
            captcha: form.captcha,
          }
        : defaultForm(),
    );
    setEditId(Date.now().toString());
    setCreating(false);
    setError(undefined);
  }, [open, form]);

  const createForm = useCallback(async () => {
    if (creating || !selectedAccount) return;
    setCreating(true);

    try {
      const form: SimpleForm = {
        name: data.name?.trim() ?? '',
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
        createdBy: firebaseUser?.uid ?? '',
        fields: (data.fields?.map((f) => {
          const newField = { ...f };
          if (newField._id) delete (newField as any)._id;
          return newField;
        }) ?? []) as FormField[],
        button: {
          title: data.buttonTitle?.trim() ?? '',
        },
        postSubmission: data.postSubmission as PostSubmission,
        status: 'active',
        preActionText: data.preActionText || '',
        afterSubmission: data.afterSubmission || {},
        captcha: data.captcha,
        formType: 'simple',
      };
      if (!data.name?.trim()) {
        show('Name is required');
        return;
      }
      if (!data.buttonTitle?.trim()) {
        show('Button title is required');
        return;
      }
      if (!data.fields?.length) {
        show('Add at least one field');
        return;
      }
      if (data.fields?.find((f) => !f.id)) {
        show('One of the fields is missing an id');
        return;
      }
      if (
        Object.values(
          groupBy(data.fields?.map((f) => f.id) || [], (a) => a),
        ).some((x) => x.length > 1)
      ) {
        show('You have multiple fields with the same id');
        return;
      }

      if (formId) {
        await updateForm(selectedAccount.id, formId, form);
      } else {
        await createFormApi(selectedAccount.id, form);
      }
      onClose();
    } catch (e: any) {
      Sentry.captureException(e);
      console.error(e);
      setError(e);
    } finally {
      setCreating(false);
    }
  }, [
    creating,
    data.afterSubmission,
    data.buttonTitle,
    data.captcha,
    data.fields,
    data.name,
    data.postSubmission,
    data.preActionText,
    firebaseUser?.uid,
    formId,
    onClose,
    selectedAccount,
    show,
  ]);

  const actions: ModalActionData[] = useMemo(
    () => [
      {
        title: 'Cancel',
        type: 'secondary',
        disabled: creating,
        onClick: () => onClose(),
      },
      {
        title: formId ? 'Update' : 'Create',
        type: 'primary',
        loading: creating,
        onClick: () => createForm(),
      },
    ],
    [creating, formId, onClose, createForm],
  );

  return (
    <Modal open={open} onClose={onClose} actions={actions} width="md">
      <DialogTitle
        as="h3"
        className="text-base font-semibold leading-6 text-gray-900"
      >
        {formId ? 'Update form' : 'Create a new form'}
      </DialogTitle>
      <div className="mt-4 flex flex-col gap-4 pb-12">
        <TextField
          title="SimpleForm name"
          readOnly={creating}
          value={data.name}
          onChange={(e) => {
            setData((d) => {
              d.name = e.target.value;
              return { ...d };
            });
          }}
          validation={{
            status: !data.name && data.name !== undefined ? 'error' : undefined,
            text:
              !data.name && data.name !== undefined
                ? 'Name is required'
                : undefined,
          }}
        />

        <div className="flex flex-col gap-1">
          <div className="flex flex-row items-center justify-between">
            <label className="block text-sm font-medium leading-6 text-gray-900">
              Fields
            </label>
            <Button
              size="x-small"
              variant="ternary"
              color="primary"
              onClick={() => setShowAdvanced(!showAdvanced)}
            >
              {showAdvanced ? 'Hide Advanced' : 'Show Advanced'}
            </Button>
          </div>
          <SortableList
            items={data.fields ?? []}
            hideDivider
            itemClassName={
              'overflow-visible rounded-md bg-white px-4 py-4 shadow mb-3'
            }
            renderItemContent={(item) => {
              return (
                <FormFieldItem
                  field={item}
                  fields={data.fields || []}
                  readOnly={creating}
                  editting={editingFields[item._id]}
                  showAdvanced={showAdvanced}
                  onChange={(field) => {
                    setData((d) => ({
                      ...d,
                      fields: [
                        ...(d.fields ?? []).map(
                          (f) =>
                            ({
                              ...f,
                              ...(f._id === field._id ? field : {}),
                            }) as any,
                        ),
                      ],
                    }));
                  }}
                  onDelete={() => {
                    setData((d) => ({
                      ...d,
                      fields: [
                        ...(d.fields ?? []).filter((f) => f._id !== item._id),
                      ],
                    }));
                  }}
                  onEdit={() => {
                    setEditingFields((v) => ({
                      ...v,
                      [item._id]: true,
                    }));
                  }}
                  onDone={() => {
                    setEditingFields((v) => ({
                      ...v,
                      [item._id]: false,
                    }));
                  }}
                />
              );
            }}
            onChange={(items) => {
              setData((d) => ({
                ...d,
                fields: items,
              }));
            }}
          />
          <div className="flex justify-end">
            <Button
              onClick={() => {
                const newId = uid(8);
                setData((d) => ({
                  ...d,
                  fields: [
                    ...(d.fields ?? []),
                    { _id: newId, id: newId, type: 'text' as const, label: '' },
                  ],
                }));
                setEditingFields((v) => ({ ...v, [newId]: true }));
              }}
            >
              Add
            </Button>
          </div>
        </div>
        <TextField
          title="Submit button title"
          readOnly={creating}
          value={data.buttonTitle}
          onChange={(e) => {
            setData((d) => {
              d.buttonTitle = e.target.value;
              return { ...d };
            });
          }}
          validation={{
            status:
              !data.buttonTitle && data.buttonTitle !== undefined
                ? 'error'
                : undefined,
            text:
              !data.buttonTitle && data.buttonTitle !== undefined
                ? 'Submit button title is required'
                : undefined,
          }}
        />
        <Select
          title="After submission"
          placeholder="What will happen after submission"
          selected={data.postSubmission?.action}
          disabled={creating}
          onChange={(e: string) => {
            setData((d) => {
              let obj = d.postSubmission;
              if (!obj) obj = d.postSubmission = {};
              obj.action = e;
              return { ...d };
            });
          }}
          options={[
            {
              value: 'none',
              child: 'Do nothing',
            },
            {
              value: 'openLink',
              child: 'Open link',
            },
            {
              value: 'openCalendly',
              child: 'Open Calendly',
            },
          ]}
        />
        {data.postSubmission?.action === 'openCalendly' ? (
          <>
            <TextField
              title="Calendly link"
              readOnly={creating}
              value={data.postSubmission.calendlyLink}
              onChange={(e) => {
                setData((d) => {
                  let obj = d.postSubmission;
                  if (!obj) obj = d.postSubmission = {};
                  obj.calendlyLink = e.target.value;
                  return { ...d };
                });
              }}
            />
          </>
        ) : (
          <></>
        )}
        {data.postSubmission?.action === 'openLink' ? (
          <>
            <TextField
              title="Link"
              type="url"
              readOnly={creating}
              value={data.postSubmission?.link}
              onChange={(e) => {
                setData((d) => {
                  let obj = d.postSubmission;
                  if (!obj) obj = d.postSubmission = {};
                  obj.link = e.target.value;
                  return { ...d };
                });
              }}
              validation={formFieldUrlValidator(data.postSubmission?.link)}
            />
          </>
        ) : (
          <></>
        )}
        <QuillField
          key={editId + '-pre-button'}
          title="Pre button agreement text (optional)"
          readOnly={creating}
          value={data.preActionText}
          placeholder="Example: by clicking here you agree to..."
          onChange={(e) => {
            setData((d) => {
              d.preActionText = e as any;
              return { ...d };
            });
          }}
        />
        <QuillField
          key={editId + '-after-submission-title'}
          title="Thank you title"
          readOnly={creating}
          value={data.afterSubmission?.title}
          placeholder="Defaults to: Submitted"
          onChange={(e) => {
            setData((d) => {
              if (!d.afterSubmission) {
                d.afterSubmission = {};
              }

              d.afterSubmission.title = e;
              return { ...d };
            });
          }}
        />

        <div className="mt-2">
          <Toggle
            label="Invisible reCAPTCHA"
            checked={data.captcha?.enabled}
            onChange={(checked) => {
              setData((d) => {
                if (!d.captcha) {
                  d.captcha = { enabled: checked };
                } else {
                  d.captcha.enabled = checked;
                }
                return { ...d };
              });
            }}
          />
        </div>
      </div>
      {error ? (
        <div className="mt-5 w-full text-end text-red-600">
          {error.message || 'Failed to create form'}
        </div>
      ) : (
        <></>
      )}
    </Modal>
  );
}

export interface FormFieldItemProps {
  field: EditableFormField;
  fields: EditableFormField[];
  readOnly: boolean;
  editting?: boolean;
  showAdvanced?: boolean;
  onChange: (_: EditableFormField) => void;
  onDelete: () => void;
  onEdit: () => void;
  onDone: () => void;
}

export function FormFieldItem({
  field,
  fields,
  readOnly,
  editting,
  showAdvanced,
  onChange,
  onDelete,
  onEdit,
  onDone,
}: FormFieldItemProps) {
  if (!editting) {
    return (
      <div className="group/form-field flex w-full items-center gap-2">
        <span className="flex items-center gap-1">
          {getFormFieldTypeIcon(field.type)}
          {getFormFieldTypeLabel(field.type)}
        </span>
        <span className="mt-0.5 flex flex-1 items-center text-sm text-gray-500">
          {field.label}
          {field.label && field.required ? '*' : ''}
        </span>
        <IconButton
          variant="ternary"
          onClick={() => onEdit()}
          className="invisible group-hover/form-field:visible"
        >
          <PencilIcon className="h-4 w-4" />
        </IconButton>
        <IconButton
          variant="ternary"
          onClick={() => onDelete()}
          className="invisible group-hover/form-field:visible"
        >
          <TrashIcon className="h-4 w-4 text-red-600" />
        </IconButton>
      </div>
    );
  }
  return (
    <div className="flex w-full flex-col gap-4">
      <FormFieldItemForm
        field={field}
        fields={fields}
        readOnly={readOnly}
        showAdvanced={showAdvanced}
        onChange={(f) => {
          onChange(f);
        }}
      />
      <div className="flex justify-end gap-1">
        <IconButton onClick={onDone} variant="ternary" size="small">
          <CheckIcon className="h-4 w-4 text-green-500" />
        </IconButton>
        <IconButton onClick={() => onDelete()} variant="ternary" size="small">
          <TrashIcon className="h-4 w-4 text-red-500" />
        </IconButton>
      </div>
    </div>
  );
}

interface FormFieldItemFormProps {
  field: EditableFormField;
  fields: EditableFormField[];
  readOnly: boolean;
  showAdvanced?: boolean;
  onChange: (_: EditableFormField) => void;
}

export function FormFieldItemForm({
  field,
  fields,
  readOnly,
  showAdvanced,
  onChange,
}: FormFieldItemFormProps) {
  return (
    <div className="flex w-full flex-col gap-4">
      <Select<FormFieldType>
        placeholder="Field type"
        selected={field.type}
        disabled={readOnly}
        onChange={(e) => {
          onChange({
            ...field,
            type: e,
          } as any);
        }}
        options={formFieldTypes.map((type) => ({
          value: type,
          child: (
            <span className="flex items-center gap-2">
              {getFormFieldTypeIcon(type)}
              {getFormFieldTypeLabel(type)}
            </span>
          ),
        }))}
      />
      <TextField
        placeholder="label"
        title="Label"
        readOnly={readOnly}
        titlePosition="overlapping"
        value={field.label || ''}
        onChange={(e) => {
          onChange({
            ...field,
            label: e.target.value,
          });
        }}
      />

      {showAdvanced && (
        <>
          <TextField
            title="Id*"
            placeholder="id"
            readOnly={readOnly}
            titlePosition="overlapping"
            value={field.id || ''}
            validation={{
              status:
                fields.filter((_field) => _field.id === field.id).length > 1 ||
                !field.id ||
                !/^[A-Za-z0-9]+$/.test(field.id)
                  ? 'error'
                  : undefined,
              text:
                fields.filter((_field) => _field.id === field.id).length > 1
                  ? 'You already have a field with this id'
                  : !field.id
                    ? 'Id is required'
                    : !/^[A-Za-z0-9]+$/.test(field.id)
                      ? 'Id must contain only letters and numbers'
                      : undefined,
            }}
            onChange={(e) => {
              onChange({
                ...field,
                id: e.target.value.trim(),
              });
            }}
          />

          {(field.type === 'businessEmail' ||
            field.type === 'email' ||
            field.type === 'hidden' ||
            field.type === 'number' ||
            field.type === 'state' ||
            field.type === 'country' ||
            field.type === 'tel' ||
            field.type === 'text' ||
            field.type === 'textArea') && (
            <TextField
              placeholder="default value"
              title="Default value"
              readOnly={readOnly}
              titlePosition="overlapping"
              value={field.defaultValue || ''}
              onChange={(e) => {
                onChange({
                  ...field,
                  defaultValue: e.target.value,
                });
              }}
            />
          )}

          {(field.type === 'select' ||
            field.type === 'multiChoice' ||
            field.type === 'pictureChoice') && (
            <Select
              title="Default value"
              titlePosition="overlapping"
              placeholder="default value"
              selected={field.defaultValue}
              disabled={readOnly}
              onChange={(e) => {
                onChange({
                  ...field,
                  defaultValue: e,
                } as any);
              }}
              options={
                field.options
                  ?.map((o) =>
                    typeof o === 'string' ? { id: o, label: o, value: o } : o,
                  )
                  ?.map((option) => ({
                    value: option.value,
                    child: option.label,
                    id: option.id,
                  })) ?? []
              }
            />
          )}

          {field.type !== 'consent' && (
            <TextField
              placeholder="placeholder"
              title="Placeholder"
              readOnly={readOnly}
              titlePosition="overlapping"
              value={field.placeholder || ''}
              onChange={(e) => {
                onChange({
                  ...field,
                  placeholder: e.target.value,
                });
              }}
            />
          )}
          <TextField
            placeholder="error message"
            title="Custom error message"
            readOnly={readOnly}
            titlePosition="overlapping"
            value={field.errorMessage || ''}
            onChange={(e) => {
              onChange({
                ...field,
                errorMessage: e.target.value,
              });
            }}
          />
        </>
      )}
      {(field.type === 'select' ||
        field.type === 'multiChoice' ||
        field.type === 'pictureChoice') && (
        <OptionsBuilder field={field} onChange={onChange} />
      )}
      {(field.type === 'multiChoice' || field.type === 'pictureChoice') && (
        <Toggle
          label="Allow multiple choices"
          checked={field.multiple}
          onChange={(checked) => {
            if (readOnly) return;
            onChange({
              ...field,
              multiple: checked,
            });
          }}
        />
      )}

      {(field.type === 'multiChoice' || field.type === 'pictureChoice') &&
        field.multiple && (
          <>
            <TextField
              title="Minimum Selections"
              placeholder="Minimum Selections"
              readOnly={readOnly}
              titlePosition="overlapping"
              type="number"
              value={field.minSelections?.toString() || ''}
              onChange={(e) => {
                onChange({
                  ...field,
                  minSelections: parseInt(e.target.value) || undefined,
                });
              }}
            />
            <TextField
              title="Maximum Selections"
              placeholder="Maximum Selections"
              readOnly={readOnly}
              titlePosition="overlapping"
              type="number"
              value={field.maxSelections?.toString() || ''}
              onChange={(e) => {
                onChange({
                  ...field,
                  maxSelections: parseInt(e.target.value) || undefined,
                });
              }}
            />
          </>
        )}
      {field.type === 'tel' && (
        <>
          <div className="flex items-center gap-2">
            <Checkbox
              id={'hide-country-codes-' + field._id}
              name={'hide-country-codes-' + field._id}
              checked={field.hideCountryCode || false}
              onChange={(checked) => {
                if (readOnly) return;
                onChange({
                  ...field,
                  hideCountryCode: checked,
                });
              }}
            />
            <label
              className="block text-sm font-medium leading-6 text-gray-900"
              htmlFor={'hide-country-codes-' + field._id}
            >
              Hide country codes
            </label>
          </div>
          {!field.hideCountryCode && (
            <ChipsAutocomplete<(typeof phoneCodes)[0]>
              title="Preferred Countries"
              getValue={(c) => `${c.name} ${c.code}`}
              renderItem={(c) => (
                <span className="flex items-center gap-2">
                  <span className="text-sm">{c.flag}</span>
                  <span className="text-sm">{c.name}</span>
                  <span className="text-xs text-gray-500">{c.code}</span>
                </span>
              )}
              selected={
                (field.preferredCountryCodes ?? ['+1'])
                  .map((c) => phoneCodes.find((pc) => pc.code === c))
                  .filter(Boolean) as typeof phoneCodes
              }
              onChange={(s) => {
                if (readOnly) return;
                onChange({
                  ...field,
                  preferredCountryCodes: s.map((c) => c.code),
                });
              }}
              getOptionId={(t) => t.code}
              options={phoneCodes}
            />
          )}
        </>
      )}
      <div className="flex items-center gap-4">
        <div className="flex items-center gap-2">
          <Checkbox
            id={'required-' + field._id}
            name={'required-' + field._id}
            checked={field.required || false}
            onChange={(checked) => {
              if (readOnly) return;
              onChange({
                ...field,
                required: checked,
              });
            }}
          />
          <label
            className="block text-sm font-medium leading-6 text-gray-900"
            htmlFor={'required-' + field._id}
          >
            Required
          </label>
        </div>
        <div className="flex items-center gap-2">
          <Checkbox
            id={'protected-' + field._id}
            name={'protected-' + field._id}
            checked={field.protected || false}
            onChange={(checked) => {
              if (readOnly) return;
              onChange({
                ...field,
                protected: checked,
              });
            }}
          />
          <label
            className="block text-sm font-medium leading-6 text-gray-900"
            htmlFor={'protected-' + field._id}
          >
            Protected
          </label>
        </div>
      </div>
      {field.type === 'text' && (
        <div className="flex items-center gap-4">
          {!field.validations?.length ? (
            <Button
              size="x-small"
              variant="ternary"
              className="-ms-2"
              onClick={() => {
                onChange({
                  ...field,
                  validations: [{ type: 'regex', regex: '' }],
                });
              }}
              icon={<PlusIcon className="h-4 w-4" />}
            >
              Add validation
            </Button>
          ) : (
            <>
              <div className="flex w-full items-center gap-2">
                <TextField
                  placeholder="regex pattern"
                  title="Regex pattern"
                  readOnly={readOnly}
                  titlePosition="overlapping"
                  fullWidth
                  value={field.validations?.[0]?.regex || ''}
                  onChange={(e) => {
                    onChange({
                      ...field,
                      validations: [
                        {
                          type: 'regex',
                          regex: e.target.value,
                        },
                      ],
                    });
                  }}
                />
                <Tooltip text="Remove validation">
                  <IconButton
                    onClick={() => {
                      onChange({
                        ...field,
                        validations: [],
                      });
                    }}
                    variant="ternary"
                    size="small"
                  >
                    <XCircleIcon className="h-4 w-4 text-red-500" />
                  </IconButton>
                </Tooltip>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

type EditableOptionsFormField = (
  | SelectFormField
  | MultiChoiceFormField
  | PictureChoiceFormField
) & {
  _id: string;
};
interface OptionsBuilderProps {
  field: EditableOptionsFormField;
  onChange: (_: EditableOptionsFormField) => void;
}

const OptionsBuilder = ({ field, onChange }: OptionsBuilderProps) => {
  return (
    <div className="flex flex-col gap-2">
      <div className="flex justify-between">
        <span className="text-sm font-medium text-gray-900">Options</span>
        <IconButton
          variant="ternary"
          size="small"
          onClick={() => {
            onChange({
              ...field,
              options: [
                ...((field.options || []) as any),
                { value: '', label: '', id: uid() },
              ],
            });
          }}
        >
          <PlusIcon className="h-4 w-4" />
        </IconButton>
      </div>
      <SortableList
        items={
          field.options?.map((o) => ({
            ...(typeof o === 'string' ? { value: o, label: o, id: uid() } : o),
          })) || []
        }
        hideDivider
        hideDragHandle
        renderItemContent={(option, index) => {
          const _option =
            typeof option === 'string'
              ? { value: option, label: option }
              : option;
          return (
            <div className="group flex w-full flex-col items-stretch gap-2 rounded-md bg-white px-4 py-2 shadow">
              <TextField
                placeholder="Option label"
                title="Label"
                readOnly={false}
                value={_option.label}
                onChange={(e) => {
                  onChange({
                    ...field,
                    options: [
                      ...(field.options || []).map((o, i) =>
                        i === index
                          ? {
                              ..._option,
                              label: e.target.value,
                              value:
                                !_option.value ||
                                _option.value === _option.label
                                  ? e.target.value
                                  : _option.value,
                            }
                          : o,
                      ),
                    ] as any,
                  });
                }}
                trailing={
                  <IconButton
                    className="absolute -right-1 top-0 hidden group-hover:block"
                    variant="ternary"
                    size="small"
                    onClick={() => {
                      onChange({
                        ...field,
                        options: [
                          ...(field.options || []).filter(
                            (_, i) => i !== index,
                          ),
                        ] as any,
                      });
                    }}
                  >
                    <TrashIcon className="h-4 w-4 text-red-500" />
                  </IconButton>
                }
              />
              <TextField
                placeholder="Option value"
                title="Value"
                readOnly={false}
                value={_option.value}
                onChange={(e) => {
                  onChange({
                    ...field,
                    options: [
                      ...(field.options || []).map((o, i) =>
                        i === index ? { ..._option, value: e.target.value } : o,
                      ),
                    ] as any,
                  });
                }}
              />
              {field.type === 'pictureChoice' && (
                <>
                  <TextField
                    placeholder="Description"
                    title="Description (optional)"
                    readOnly={false}
                    value={
                      (
                        option as NonNullable<
                          PictureChoiceFormField['options']
                        >[0]
                      ).description
                    }
                    onChange={(e) => {
                      onChange({
                        ...field,
                        options: [
                          ...(field.options || []).map((o, i) =>
                            i === index
                              ? { ..._option, description: e.target.value }
                              : o,
                          ),
                        ] as any,
                      });
                    }}
                  />
                  <label className="block text-sm font-medium leading-6 text-gray-900">
                    Image
                  </label>
                  <FileUploadInput
                    types={['image']}
                    image={
                      (
                        option as NonNullable<
                          PictureChoiceFormField['options']
                        >[0]
                      ).image
                    }
                    onSelected={(image) => {
                      onChange({
                        ...field,
                        options: [
                          ...(field.options || []).map((o, i) =>
                            i === index
                              ? { ..._option, image: image || null }
                              : o,
                          ),
                        ] as any,
                      });
                    }}
                  />
                </>
              )}
            </div>
          );
        }}
        onChange={(items) => {
          onChange({
            ...field,
            options: items.map(
              (o) =>
                (typeof o === 'string'
                  ? { value: o, label: o, id: uid() }
                  : o) as any,
            ),
          });
        }}
      />
    </div>
  );
};
